import type { MyEstimateGroup, MyEstimateRequestStatus } from '@massilia/queries'

import { CalendarClock, CheckCircle2, Clock, FileSearch, MessageSquareReply } from 'lucide-react'

import { ImportantNotice, type ImportantNoticeVariant } from '@/components/ui/important-notice'

import { TariffsLaterNotice } from './tariffs-later-notice'

interface RequestStatusNoticeProps {
  group: MyEstimateGroup
}

interface StatusExplanation {
  icon: typeof Clock
  /** Le coup suivant, et par qui il est joué. */
  next: string
  /** Où en est le dossier, en une phrase. */
  state: string
  title: string
  variant: ImportantNoticeVariant
}

/**
 * Ce que chaque statut veut dire, et qui joue le coup suivant.
 *
 * Le badge dit l'état en deux mots ; il ne dit pas ce qu'on attend du client, ni
 * ce que l'agence est en train de faire. C'était la question qui revenait en
 * recette : « ma demande est-elle partie ? », « dois-je faire quelque chose ? ».
 *
 * Une seule table, deux surfaces (le panneau de l'espace client et la page
 * publique de la demande) : le même statut ne s'explique pas de deux façons.
 */
const EXPLANATIONS = {
  ACTIVE: {
    icon: MessageSquareReply,
    next: 'À vous de jouer : confirmez celle qui vous convient, demandez un ajustement, ou refusez-la.',
    state: 'Nos propositions sont prêtes et vous attendent.',
    title: 'À vous de choisir',
    variant: 'info',
  },
  CLOSED: {
    icon: CheckCircle2,
    next: 'Rien à faire. Vous pouvez déposer une nouvelle demande à tout moment.',
    state: 'Cette demande est close : plus aucune proposition n’est en attente.',
    title: 'Demande terminée',
    variant: 'success',
  },
  PROCESSING: {
    icon: FileSearch,
    next: 'Nous vous répondons sous 72 h, par e-mail et ici même. Rien à faire de votre côté.',
    state: 'Votre demande est bien arrivée, notre équipe la chiffre.',
    title: 'Nous préparons votre devis',
    variant: 'info',
  },
  RESERVATION_REQUESTED: {
    icon: Clock,
    next: 'Notre équipe la traite et revient vers vous pour finaliser — paiement compris. Rien à faire de votre côté.',
    state: 'Votre demande de réservation est partie, avec les informations des voyageurs.',
    title: 'Réservation en attente de traitement',
    variant: 'warning',
  },
  // Jamais rendu : `TARIFFS_LATER` passe par `TariffsLaterNotice`, qui porte déjà
  // l'explication ET la date. Présent pour que la table reste exhaustive — et
  // pour couvrir la demande rangée dont la date de retour est indérivable, qui
  // n'a donc aucun message d'horizon à afficher.
  TARIFFS_LATER: {
    icon: CalendarClock,
    next: 'Notre équipe revient vers vous dès qu’ils le sont. Rien à faire de votre côté.',
    state: 'Les tarifs de ce voyage ne sont pas encore publiés par les compagnies.',
    title: 'Tarifs indisponibles pour le moment',
    variant: 'warning',
  },
} satisfies Record<MyEstimateRequestStatus, StatusExplanation>

/**
 * L'encart d'état d'une demande, au-dessus de ses propositions.
 */
export function RequestStatusNotice({ group }: RequestStatusNoticeProps) {
  // Le bandeau tarifaire dit la même chose en mieux : il annonce la date.
  if (group.status === 'TARIFFS_LATER' && group.tariffsLaterMessage) {
    return <TariffsLaterNotice group={group} />
  }

  const explanation = EXPLANATIONS[group.status]

  return (
    <ImportantNotice
      icon={explanation.icon}
      title={explanation.title}
      variant={explanation.variant}
    >
      <p>{explanation.state}</p>
      <p className="mt-1">{explanation.next}</p>
    </ImportantNotice>
  )
}
